/**
 * The line-up page's URL state.
 *
 * Same rule as Tasks and Contacts: the list a person is looking at IS the address. "Alt som ennå
 * ikke er signert på Storscenen, sortert på tid" has to be a link a colleague can be sent, and a
 * reload has to land on it rather than on the unfiltered default. So the status chips, the stage
 * menu and the sort write to the address bar instead of to a `useState`, and this module — not a
 * component — decides what a valid line-up URL is.
 *
 * Absent means default. `?sort=order` and `?dir=asc` are never written, so the plain
 * `…/performances` a person reaches from the årgang is the same location the router already had
 * and it does not treat them as two.
 *
 * ---------------------------------------------------------------------------------------------
 * ONE PARAMETER FOR THE SORT, AND IT IS NOT THE GROUPING PARAMETER TASKS HAS
 *
 * Tasks folds grouping and order into one `group` parameter because in that list they are one
 * act. Nothing here is grouped: `PERFORMANCE_SORT_KEYS` is three orders over one flat line-up,
 * so `sort` is a sort and nothing else.
 *
 * `order` — the running order, `performances.sort_order` — is the default and is the one of the
 * three that no column expresses. There IS no sortOrder column on this table (see
 * `PerformancesList` for why a number nobody can choose is not a column), so `order` is reachable
 * from the "Sorter etter" control only, exactly as Tasks' "Ingen gruppering" is. `time` and
 * `status` are columns, so they are also header clicks — and a header click writes this same
 * parameter, because there is only ever one order.
 * ---------------------------------------------------------------------------------------------
 */
import {
  PERFORMANCE_SORT_KEYS,
  PERFORMANCE_STATUSES,
  isUuid,
  type PerformanceSortKey,
  type PerformanceStatus,
  type SortDirection,
} from '@tvitvi/shared';

import type { PerformanceListFilters } from '@/api/query-keys';
import type { TableSort } from '@/components/ui/Table';

export const DEFAULT_SORT: PerformanceSortKey = 'order';
export const DEFAULT_DIRECTION: SortDirection = 'asc';

/**
 * The table column each sort key is expressed by, so a header click can write the sort and the
 * sorted column can wear `aria-sort` and the arrow.
 *
 * `order` HAR EN KOLONNE NÅ (239.14, Atle 04.10: «skal kunne sorteres i tabellen også»):
 * «Rekkefølge» viser plassen som et tall, og et klikk på overskriften sorterer på den.
 */
export const SORT_COLUMNS = {
  order: 'order',
  time: 'startsAt',
  status: 'status',
} as const satisfies Record<PerformanceSortKey, string>;

/**
 * What `validateSearch` produces and what `navigate({ search })` must be handed back.
 *
 * `status` is one comma-separated parameter rather than a repeated one because that is the
 * shorter URL and `listPerformancesQuerySchema` accepts exactly this form — the link a person
 * copies out of the address bar is the query the server receives.
 */
export interface PerformancesSearch {
  status?: string;
  stage?: string;
  /**
   * FRITEKSTSØKET, OG DET LIGGER I ADRESSEN som alt annet på denne siden.
   *
   * Atle, 9. september: «ALLE TABELLER MÅ KUNNE SØKES OG SORTERES!!!» Et `useState` ville gjort
   * søket til den ene tingen på siden man ikke kunne sende videre — og «alt fra Kvitbjørn i år» er
   * nettopp en lenke en kollega får.
   */
  q?: string;
  sort?: PerformanceSortKey;
  dir?: SortDirection;
}

/** Den samme grensen kontrakten setter, så et for langt søk aldri blir en 400. */
const MAX_SEARCH = 120;

const MAX_STATUSES = PERFORMANCE_STATUSES.length;

function isStatus(value: string): value is PerformanceStatus {
  return (PERFORMANCE_STATUSES as readonly string[]).includes(value);
}

/** Accepts the comma form and the array form; returns known statuses only, de-duplicated. */
function parseStatuses(value: unknown): readonly PerformanceStatus[] {
  const parts = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : ([] as unknown[]);
  const found = parts.map((part) => (typeof part === 'string' ? part.trim() : '')).filter(isStatus);
  return [...new Set(found)].slice(0, MAX_STATUSES);
}

/**
 * Validated at the edge of the router, like `validateTasksSearch` is: no component downstream ever
 * sees a sort key the endpoint does not offer, a status the API would answer 400 for, or a stage
 * id that is not a UUID.
 *
 * An unknown value is DROPPED rather than corrected, so `?sort=artist` renders the default list
 * instead of an error page. A URL is something people edit by hand and something old links carry;
 * the page it names should still open.
 */
export function validatePerformancesSearch(raw: Record<string, unknown>): PerformancesSearch {
  const search: PerformancesSearch = {};

  const statuses = parseStatuses(raw['status']);
  if (statuses.length > 0 && statuses.length < MAX_STATUSES) search.status = statuses.join(',');

  const stage = raw['stage'];
  // A stage is a UUID or it is nothing. `listPerformancesQuerySchema.stageId` is `z.uuid()`, so
  // anything else would be a 400 rather than an empty list.
  if (typeof stage === 'string' && isUuid(stage)) search.stage = stage;

  const q = raw['q'];
  if (typeof q === 'string' && q.trim() !== '') search.q = q.trim().slice(0, MAX_SEARCH);

  const sort = raw['sort'];
  if (typeof sort === 'string' && (PERFORMANCE_SORT_KEYS as readonly string[]).includes(sort)) {
    if (sort !== DEFAULT_SORT) search.sort = sort as PerformanceSortKey;
  }

  if (raw['dir'] === 'desc') search.dir = 'desc';

  return search;
}

export function statusesOf(search: PerformancesSearch): readonly PerformanceStatus[] {
  return parseStatuses(search.status);
}

export function stageOf(search: PerformancesSearch): string | undefined {
  return search.stage;
}

export function searchOf(search: PerformancesSearch): string | undefined {
  return search.q;
}

/** The order `GET /v1/performances` is asked for. Always a key the endpoint accepts. */
export function sortOf(search: PerformancesSearch): {
  sort: PerformanceSortKey;
  direction: SortDirection;
} {
  return { sort: search.sort ?? DEFAULT_SORT, direction: search.dir ?? DEFAULT_DIRECTION };
}

/**
 * The filters `GET /v1/performances` is asked with — and therefore its cache key. Shared by the page
 * and the route's loader, which must build the very same key for the page to find its prefetch.
 */
export function performanceListFilters(
  statuses: readonly PerformanceStatus[],
  stageId: string | undefined,
  term: string | undefined,
  order: { sort: PerformanceSortKey; direction: SortDirection },
): PerformanceListFilters {
  return {
    ...(statuses.length > 0 ? { statuses } : {}),
    ...(stageId !== undefined ? { stageId } : {}),
    ...(term === undefined ? {} : { search: term }),
    sort: order.sort,
    direction: order.direction,
  };
}

/**
 * Filteret eksportene fra lineupen får (`scope.filter`): det samme spørreobjektet lista sender til
 * `GET /v1/performances`, uten årgang (den står i `params`), side og markør. API-et validerer det
 * med listas eget skjema, så statusene går som den samme kommaseparerte teksten.
 */
export function exportFilterOf(filters: PerformanceListFilters): Record<string, string> {
  return {
    sort: filters.sort ?? DEFAULT_SORT,
    direction: filters.direction ?? DEFAULT_DIRECTION,
    ...(filters.stageId === undefined ? {} : { stageId: filters.stageId }),
    ...(filters.statuses === undefined || filters.statuses.length === 0
      ? {}
      : { status: filters.statuses.join(',') }),
    ...(filters.search === undefined ? {} : { search: filters.search }),
  };
}

/** What `Table` is handed, so the sorted column wears `aria-sort` and the arrow (239.14). */
export function tableSortOf(search: PerformancesSearch): TableSort | null {
  const { sort, direction } = sortOf(search);
  return { columnId: SORT_COLUMNS[sort], direction };
}

/**
 * The next URL for the "Sorter etter" control.
 *
 * A keyset cursor is minted for one sort spec and `decodeCursor` refuses it under any other
 * (`packages/shared/src/http/pagination.ts`), so carrying a position across a change of order is
 * not a stale-data problem, it is a 400. Nothing is carried: what comes back holds the filters,
 * and a position is not one of them. Should this page ever grow a `?cursor=`, this is the
 * function that has to drop it.
 */
export function withSort(search: PerformancesSearch, sort: PerformanceSortKey): PerformancesSearch {
  const next: PerformancesSearch = { ...search };
  if (sort === DEFAULT_SORT) delete next.sort;
  else next.sort = sort;
  if (next.dir === DEFAULT_DIRECTION) delete next.dir;
  return next;
}

/** The next URL for a header click. A header the API cannot order by writes nothing. */
export function withTableSort(search: PerformancesSearch, sort: TableSort): PerformancesSearch {
  const key = (Object.keys(SORT_COLUMNS) as (keyof typeof SORT_COLUMNS)[]).find(
    (candidate) => SORT_COLUMNS[candidate] === sort.columnId,
  );
  if (key === undefined) return search;
  return withSort({ ...search, dir: sort.direction }, key);
}

/**
 * «Status ▾»: slår én status AV eller PÅ i menyen (bred QA 18. september, funn 8).
 *
 * ---------------------------------------------------------------------------------------------
 * FRAVÆRET BETYR ALLE, OG DET ER DERFOR DENNE IKKE ER EN VANLIG VEKSLER.
 *
 * Så lenge chipsene sto i linja var handlingen «vis BARE denne»: ingen valgt betydde alle, og et
 * klikk la én til. En avkryssingsmeny stiller det motsatte spørsmålet — alle ti står som huket av
 * når ingenting er filtrert — så et klikk på «Avlyst» må bety «vis alt UNNTATT avlyst», altså de
 * ni andre. Starter man fra den tomme lista i stedet, ville klikket skrudd av de ni man ikke
 * rørte.
 *
 * Alle ti på skrives fortsatt som FRAVÆR av parameteren: `?status=…` med alle verdiene er den
 * samme lista som ingen parameter, og en adresse som bærer standarden ser filtrert ut.
 *
 * Samme form som kalenderens `typeChecks`, som er den andre `ToolbarChecks` i produktet.
 */
export function withStatus(
  search: PerformancesSearch,
  status: PerformanceStatus,
): PerformancesSearch {
  const current = statusesOf(search);
  const shown = current.length === 0 ? PERFORMANCE_STATUSES : current;
  const next = shown.includes(status)
    ? shown.filter((value) => value !== status)
    : [...shown, status];

  const result: PerformancesSearch = { ...search };
  if (next.length > 0 && next.length < MAX_STATUSES) result.status = next.join(',');
  else delete result.status;
  return result;
}

/** The stage menu. `undefined` is "every stage", which is the absence of the parameter. */
export function withStage(
  search: PerformancesSearch,
  stageId: string | undefined,
): PerformancesSearch {
  const next: PerformancesSearch = { ...search };
  if (stageId === undefined) delete next.stage;
  else next.stage = stageId;
  return next;
}

/** Everything except the order, which is a way of looking rather than a filter. */
export function withoutFilters(search: PerformancesSearch): PerformancesSearch {
  const next: PerformancesSearch = { ...search };
  delete next.status;
  delete next.stage;
  return next;
}

/** True when the list has been narrowed — which changes the empty copy (DESIGN.md §8). */
export function isFiltered(search: PerformancesSearch): boolean {
  return (
    statusesOf(search).length > 0 || stageOf(search) !== undefined || searchOf(search) !== undefined
  );
}
